import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import useStore from '../../store/useStore';
import { orchard } from '../../theme';
import CherryMark from '../CherryMark';
import Demo, { Stage } from './Demos';
import { getProgress, setLearnFlags } from './learnApi';
import { LESSONS } from './lessons';

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(-4deg); } 50% { transform: translateY(-10px) rotate(4deg); }
`;

function Hero() {
  const bits = ['🎵', '📄', '🎨', '⭐', '📦', '🎤'];
  return (
    <Stage h={190}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Box sx={{ animation: `${float} 4s ease-in-out infinite` }}>
          <CherryMark size={86} />
        </Box>
      </Box>
      {bits.map((b, i) => (
        <Box
          key={b}
          sx={{
            position: 'absolute',
            fontSize: 26,
            left: `${[10, 78, 18, 84, 6, 70][i]}%`,
            top: `${[14, 12, 64, 60, 40, 80][i]}%`,
            animation: `${float} ${3 + i * 0.4}s ${i * 0.3}s ease-in-out infinite`,
          }}
        >
          {b}
        </Box>
      ))}
    </Stage>
  );
}

function Steps({ total, en }) {
  return (
    <Stage h={190} tint={orchard.honey}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          textAlign: 'center',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: orchard.fontDisplay,
              fontWeight: 700,
              fontSize: '3rem',
              lineHeight: 1,
              color: orchard.cherry,
            }}
          >
            {total}
          </Typography>
          <Typography sx={{ fontWeight: 800 }}>
            {en ? 'lessons · 1–3 minutes' : 'уроков · 1–3 минуты'}
          </Typography>
          <Box
            sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mt: 1 }}
          >
            {Array.from({ length: 6 }, (_, i) => (
              <Box
                key={i}
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  bgcolor:
                    i < 2
                      ? '#2E7D5B'
                      : (t) => alpha(t.palette.text.primary, 0.15),
                  color: '#fff',
                  fontSize: 12,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {i < 2 ? '✓' : ''}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Stage>
  );
}

const SLIDES = [
  {
    visual: () => <Hero />,
    ru: [
      'Добро пожаловать в Cherry-Pick Core',
      'Здесь всё для ваших уроков: расписание, дети, материалы — и Черри, помощник, который берёт рутину на себя и помогает творить.',
    ],
    en: [
      'Welcome to Cherry-Pick Core',
      'Everything for your lessons: the schedule, the children, materials — and Cherry, an assistant who takes the routine off your hands and helps you create.',
    ],
  },
  {
    visual: () => <Demo kind="day" />,
    ru: [
      'Ваш день — на одном экране',
      'Утром откройте «Сегодня»: уроки по времени, тема, слова и наборы к урокам. После урока — одна отметка и пара слов о том, что сделали дети.',
    ],
    en: [
      'Your day on one screen',
      'Open Today in the morning: lessons by time, topic, words and lesson sets. After a lesson — one tick and a few words about what the children made.',
    ],
  },
  {
    visual: () => <Demo kind="chat" />,
    ru: [
      'Черри — ваш помощник',
      'Пишите или говорите: план урока, домашка, письмо родителям, рабочий лист. Черри знает ваших детей и план. Есть и в Telegram.',
    ],
    en: [
      'Cherry, your assistant',
      'Type or talk: a lesson plan, homework, a note to parents, a worksheet. Cherry knows your children and the plan. Also in Telegram.',
    ],
  },
  {
    visual: () => <Demo kind="karaoke" />,
    ru: [
      'Творите за минуту',
      'Песня из стишка с караоке, рабочие листы, картинки, наклейки с лицами детей — и всё это в набор к уроку.',
    ],
    en: [
      'Create in a minute',
      'A song from a rhyme with karaoke, worksheets, pictures, stickers with the children’s faces — all into a set for the lesson.',
    ],
  },
  {
    visual: (en) => <Steps total={LESSONS.length} en={en} />,
    ru: [
      'Учитесь по шагам',
      'Короткие уроки с подсказками прямо на экране. Каждый отмечается сам, когда вы попробуете это по-настоящему. Начните с первого — пара минут.',
    ],
    en: [
      'Learn step by step',
      'Short lessons with hints right on the screen. Each ticks itself when you really try it. Start with the first — a couple of minutes.',
    ],
  },
];

/* First visit of a new teacher (or «Показать приветствие» in «Обучение»). */
export default function WelcomeDialog({ open, onClose }) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { language } = useStore();
  const en = language === 'en';
  const [i, setI] = useState(0);
  useEffect(() => {
    if (open) setI(0);
  }, [open]);
  const s = SLIDES[i];
  const [title, text] = en ? s.en : s.ru;
  const last = i === SLIDES.length - 1;
  const finish = (go) => {
    setLearnFlags({ welcome: true }).catch(() => {});
    onClose();
    if (go) navigate('/learn');
  };
  return (
    <Dialog
      open={open}
      onClose={() => finish(false)}
      fullWidth
      maxWidth="xs"
      fullScreen={phone}
      PaperProps={{ sx: { borderRadius: phone ? 0 : '24px' } }}
    >
      <Box
        data-welcome
        sx={{
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          minHeight: phone ? '100%' : 480,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="overline"
            sx={{ flex: 1, color: 'text.secondary', fontSize: '0.66rem' }}
          >
            {i + 1} / {SLIDES.length}
          </Typography>
          <IconButton
            aria-label={en ? 'Close' : 'Закрыть'}
            onClick={() => finish(false)}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box key={i} sx={{ animation: 'none' }}>
          {s.visual(en)}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: orchard.fontDisplay,
              fontWeight: 700,
              fontSize: '1.45rem',
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {text}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75 }}>
          {SLIDES.map((_, k) => (
            <Box
              key={k}
              component="button"
              aria-label={`${k + 1}`}
              onClick={() => setI(k)}
              sx={{
                width: k === i ? 22 : 8,
                height: 8,
                p: 0,
                border: 0,
                borderRadius: 4,
                cursor: 'pointer',
                bgcolor:
                  k === i
                    ? orchard.cherry
                    : (t) => alpha(t.palette.text.primary, 0.2),
                transition: 'width .3s',
              }}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!last && (
            <Button
              onClick={() => finish(false)}
              sx={{ textTransform: 'none' }}
            >
              {en ? 'Skip' : 'Пропустить'}
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          {i > 0 && (
            <Button onClick={() => setI(i - 1)} sx={{ textTransform: 'none' }}>
              {en ? 'Back' : 'Назад'}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => (last ? finish(true) : setI(i + 1))}
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 800,
              px: 2.5,
            }}
          >
            {last
              ? en
                ? 'Start learning'
                : 'Начать обучение'
              : en
                ? 'Next'
                : 'Далее'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

/* Layout: opens the welcome once for a new teacher. */
export function WelcomeGate() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let stop = false;
    if (window.location.pathname === '/learn') return undefined;
    getProgress()
      .then((p) => !stop && p.showWelcome && setOpen(true))
      .catch(() => {});
    return () => {
      stop = true;
    };
  }, []);
  return <WelcomeDialog open={open} onClose={() => setOpen(false)} />;
}
